import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { preloadImage, idlePrefetchImages } from "@/utils/imagePreload";
import PhilosophyStage from "@/components/site/PhilosophyStage";
import ScenesOfHomes from "@/components/site/ScenesOfHomes";
import RecognitionPress from "@/components/site/RecognitionPress";
import ProcessTable from "@/components/site/ProcessTable";
import Studio from "@/components/site/Studio";
import WeShape from "@/components/site/WeShape";
import ChapterPill from "@/components/site/ChapterPill";
import Footer from "@/components/site/Footer";
import HlsMobileMenu from "@/components/site/HlsMobileMenu";
import "@/components/site/HlsMobileMenu.css";
import "@/pages/HeroLightStudy.css";

const FRAMES = [
  "/assets/homepage/hero/01-quiet-dim-opening-3200.jpg",
  "/assets/homepage/hero/02-first-light-3200.jpg",
  "/assets/homepage/hero/03-soft-directional-light-3200.jpg",
  "/assets/homepage/hero/04-intermediate-pattern-daylight-3200.jpg",
  "/assets/homepage/hero/05-restrained-patterned-daylight-3200.jpg",
];

// First image users see after the hero — eligible for idle prefetch
// once the hero is ready, so the next scroll beat feels immediate.
const SCENE_01_IMG =
  "/assets/homepage/scenes/01-immersive-3200.jpg";

const DURATIONS = [0, 1600, 1600, 1800, 2000];

// Deliberate hold on the locked first intro state (image 1 + brand
// wordmark + descriptor) so the title reads cleanly before the
// crossfade sequence begins. Applied uniformly to every desktop
// timer after `setEntered` so the gaps between later transitions
// remain unchanged. Bypassed entirely when `skipIntro` is true.
const DESKTOP_FIRST_FRAME_HOLD_MS = 1700;

// Mobile/tablet breakpoint — must match the value used elsewhere
// in this page's CSS responsive section.
const MOBILE_MAX = 880;

// Compressed mobile timeline. The first frame is held deliberately
// so the user can read "Ōraṇi" + the descriptor
// before the image crossfade sequence starts. The relative gaps
// between later transitions are unchanged.
const MOBILE_T = {
  frame2: 2600,
  frame3: 3200,
  descriptorOut: 3200,
  brandTop: 3500,
  frame4: 4000,
  frame5: 4600,
  revealed: 5100,
  scrollCue: 5400,
  navVisible: 5800,
};

// First-frame hold after the readiness signals fire — matches the
// 300ms delay before the first animation event on desktop
// (`setEntered` at 300ms after `heroReady`). Keeping the same value
// on mobile ensures the opening beat reads consistently across
// breakpoints; we do not invent a longer hold on phones.
const MOBILE_HOLD_MS = 300;

// Hard ceiling on the image-readiness wait so a flaky CDN never
// freezes the intro forever.
const MOBILE_IMAGE_TIMEOUT_MS = 5500;

// Hard ceiling on the paint-readiness wait so an unusual browser
// without FCP / IO / load support never freezes the intro forever.
const MOBILE_PAINT_SAFETY_MS = 6000;

export default function HeroLightStudy() {
  const reducedMotion = useReducedMotion();

  // Detect mobile/tablet synchronously on first render so the very
  // first paint already reflects the correct initial state (image 1
  // + brand visible centre, nothing else). Resizing across the
  // breakpoint at runtime is rare for end-users; we still listen so
  // dev-tools toggles behave sensibly.
  const computeIsMobile = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
  };
  const [isMobile, setIsMobile] = useState(computeIsMobile);

  // Scroll-restoration only check. Separate from `computeSkipIntro`
  // because the scroll-restoration catcher (`useLayoutEffect` below)
  // must only fire when the browser placed the user past the hero —
  // never when the session-seen flag is set during a fresh-load
  // intro replay, otherwise the catcher would snap mid-animation.
  const scrollIsPastHero = () => {
    if (typeof window === "undefined") return false;
    return window.scrollY > window.innerHeight * 0.5;
  };

  // If the page is reloaded at a scroll position past the Hero (browser
  // scroll restoration), we must NOT play the Hero intro — otherwise the
  // brand/subtext would fade in over later sections and animate up into
  // the navbar. We detect that synchronously on first render so the very
  // first paint already reflects the post-intro state.
  //
  // We ALSO skip the intro when the user has already seen it in this
  // browser session — i.e. they've navigated to the homepage via SPA
  // client-side navigation from another internal page (Press, Project,
  // Enquire, Careers, etc.), or via browser back/forward, or by
  // reloading a non-homepage path and then coming Home. The intro
  // belongs to the very first visit of the homepage in a session and
  // to manual reloads of `/` itself — anything else feels jarring.
  //
  // Manual reload of `/` clears the session flag in index.js (and
  // resets scroll to top) so the intro replays cleanly from the hero.
  //
  // Note: in SPAs, browser scroll restoration sometimes happens AFTER
  // React's first render (because the page's full scrollHeight only
  // exists once we render the sections). So we ALSO re-check in a
  // useLayoutEffect below. The intro's first visible step (`setEntered`
  // at 300ms) is gated by `skipIntro`, so any pre-300ms detection is
  // silent — nothing has had a chance to animate yet.
  const computeSkipIntro = () => {
    if (typeof window === "undefined") return false;

    // 1. Scroll restoration past the hero — always skip (the intro
    //    cannot run sensibly when the user is already mid-page).
    if (window.scrollY > window.innerHeight * 0.5) return true;

    // 2. Session-level replay control.
    let docLoadedOnHome = true;
    try {
      const initialPath =
        window.__THS_INITIAL_PATH__ || window.location.pathname;
      docLoadedOnHome =
        initialPath === "/" || initialPath === "/hero-light-study";
    } catch (_e) {
      /* fall through */
    }

    // If the document itself was loaded on a non-home path (e.g.
    // /press, then user navigated Home via SPA), the intro should
    // never play — the hero is mounting for the first time in this
    // document but the user has been browsing for a while.
    if (!docLoadedOnHome) return true;

    // Document was loaded on the homepage. If the intro has already
    // been seen in this session, skip — the user is coming back via
    // SPA navigation, browser back/forward, or any non-reload route
    // back to `/`. Manual reload of `/` clears this flag in index.js,
    // so reloads always play the intro.
    try {
      if (sessionStorage.getItem("ths_home_intro_seen") === "1") {
        return true;
      }
    } catch (_e) {
      /* sessionStorage unavailable — fall through and play intro */
    }

    return false;
  };
  const [skipIntro, setSkipIntro] = useState(computeSkipIntro);

  // Hero readiness gate. The opening animation timeline must not start
  // until the hero image is fetched + decoded — otherwise the brand →
  // nav transition can finish over a still-blank canvas. If we are
  // already past the hero (scroll-restored to a later section), there
  // is no animation to gate, so we resolve ready immediately.
  //
  // On mobile with prefers-reduced-motion, we also render the settled
  // hero state from the FIRST paint (no opening flash, no intro, no
  // freeze) — see `settledInitial` below.
  const settledInitial = skipIntro || (isMobile && reducedMotion);

  const [heroReady, setHeroReady] = useState(settledInitial);

  // Mobile-only visual-readiness gate. Holds the opening state on
  // screen until the user has actually seen it (FCP + image 1
  // decoded), then waits MOBILE_HOLD_MS (matches the desktop
  // opening cadence) before the timeline begins. Skipped entirely
  // when settledInitial is true (scroll restored OR mobile +
  // reduced-motion).
  const [mobileGate, setMobileGate] = useState(settledInitial);

  // On mobile fresh loads (reducedMotion=false), render the locked
  // opening state from the first paint: image 1 visible, brand
  // wordmark visible centred, descriptor visible, NO title / cue /
  // hamburger / brand-at-top. Achieved by initialising `entered`
  // true on mobile from the start (CSS transitions don't fire
  // because `.is-entered` is applied at mount).
  const initialEntered = settledInitial || (isMobile && !reducedMotion);

  const [frame, setFrame] = useState(settledInitial ? 5 : 1);
  const [entered, setEntered] = useState(initialEntered);
  const [descriptorOut, setDescriptorOut] = useState(settledInitial);
  const [brandTop, setBrandTop] = useState(settledInitial);
  const [navVisible, setNavVisible] = useState(settledInitial);
  const [revealed, setRevealed] = useState(settledInitial);
  const [ctaVisible, setCtaVisible] = useState(settledInitial);
  const [onLight, setOnLight] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const [scrollCueVisible, setScrollCueVisible] = useState(settledInitial);
  // When the intro is skipped after the brand/sub may have already begun
  // their CSS opacity transitions, we briefly disable all transitions on
  // the brand-mark + nav so the snap is visually instant (no fade-out
  // flash). Cleared on the next paint frame so genuine future transitions
  // (e.g., nav fading back in at the final CTA) still animate.
  const [snapInstant, setSnapInstant] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);
  const navigate = useNavigate();

  // Track mobile/tablet viewport changes (mainly for dev-tools toggles).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Mark the homepage as "intro seen" for this session as soon as the
  // hero mounts. Subsequent mounts of this component (SPA navigation
  // back to /, browser back/forward, etc.) will read this flag in
  // `computeSkipIntro` and render the post-intro hero state directly.
  // Manual reload of `/` clears this flag (see index.js) so the intro
  // replays cleanly from the top.
  useEffect(() => {
    try {
      sessionStorage.setItem("ths_home_intro_seen", "1");
    } catch (_e) {
      /* sessionStorage unavailable — non-fatal */
    }
  }, []);

  // Preload + decode the first hero frame before any opening animation
  // is scheduled. The safety timeout inside preloadImage ensures the
  // page never gets stuck waiting on a flaky CDN — after ~4.5s we
  // proceed regardless and fall back to the dark base background.
  // Desktop-only: mobile runs its own multi-image readiness gate
  // (see the mobile gate effect below).
  useEffect(() => {
    if (skipIntro || isMobile) return undefined;
    let cancelled = false;
    preloadImage(FRAMES[0]).then(() => {
      if (!cancelled) setHeroReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [skipIntro, isMobile]);

  // -------- Mobile visual-readiness gate --------
  // Holds the locked opening state (image 1 + brand centre) on screen
  // until the user has actually seen it, then adds a small hold
  // matching the desktop opening cadence before the timeline begins.
  //
  // The key signal is FIRST-CONTENTFUL-PAINT, not document.readyState.
  // On iPhone Chrome the browser keeps the page blank/white during
  // initial load while JS continues executing — readyState + rAF +
  // setTimeout can all fire during that blank phase, telescoping the
  // intro into invisible territory. FCP only fires once the browser
  // has actually painted visible content, so we hang the hold timer
  // off that moment. IntersectionObserver on the hero element and
  // window.load act as fallbacks for environments without FCP support.
  useEffect(() => {
    if (!isMobile || skipIntro || reducedMotion) return undefined;

    let cancelled = false;
    let holdTimer = null;
    let imgSafetyId = 0;
    let paintSafetyId = 0;
    const cleanups = [];

    // ---- Signal 1: image 1 loaded + decoded (or safe timeout). ----
    const img1Loaded = new Promise((resolve) => {
      const img = new Image();
      img.decoding = "async";
      const done = () => resolve();
      img.onload = () => {
        if (typeof img.decode === "function") {
          img.decode().then(done, done);
        } else {
          done();
        }
      };
      img.onerror = done;
      img.src = FRAMES[0];
    });
    const img1Safety = new Promise((resolve) => {
      imgSafetyId = window.setTimeout(resolve, MOBILE_IMAGE_TIMEOUT_MS);
    });

    // Background prefetch for the rest of the hero frames so the
    // timeline crossfades have content when they fire. Best-effort —
    // we don't gate on these.
    FRAMES.slice(1).forEach((src) => {
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    });

    // ---- Signal 2: first user-visible paint. ----
    // FCP (preferred) > IntersectionObserver on hero > window.load.
    // Whichever fires first resolves this promise.
    const painted = new Promise((resolve) => {
      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };

      try {
        const existing = performance.getEntriesByName(
          "first-contentful-paint"
        );
        if (existing && existing.length > 0) {
          done();
          return;
        }
        const po = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === "first-contentful-paint") {
              po.disconnect();
              done();
              return;
            }
          }
        });
        po.observe({ type: "paint", buffered: true });
        cleanups.push(() => po.disconnect());
      } catch (_e) {
        // FCP unsupported — fall through to IO / load.
      }

      if (heroRef.current && "IntersectionObserver" in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            done();
          }
        });
        io.observe(heroRef.current);
        cleanups.push(() => io.disconnect());
      }

      const onLoad = () => {
        requestAnimationFrame(() => requestAnimationFrame(done));
      };
      if (document.readyState === "complete") {
        onLoad();
      } else {
        window.addEventListener("load", onLoad, { once: true });
        cleanups.push(() =>
          window.removeEventListener("load", onLoad)
        );
      }

      paintSafetyId = window.setTimeout(done, MOBILE_PAINT_SAFETY_MS);
    });

    Promise.all([Promise.race([img1Loaded, img1Safety]), painted]).then(
      () => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          requestAnimationFrame(() => {
            if (cancelled) return;
            holdTimer = window.setTimeout(() => {
              if (cancelled) return;
              setMobileGate(true);
            }, MOBILE_HOLD_MS);
          });
        });
      }
    );

    return () => {
      cancelled = true;
      if (holdTimer) window.clearTimeout(holdTimer);
      if (imgSafetyId) window.clearTimeout(imgSafetyId);
      if (paintSafetyId) window.clearTimeout(paintSafetyId);
      cleanups.forEach((fn) => fn());
    };
  }, [isMobile, skipIntro, reducedMotion]);

  // Once the hero is ready, quietly idle-prefetch the next critical
  // images so later sections feel instant. Never runs during the
  // initial hero gate, never blocks user interaction. On mobile the
  // gate already loads all five hero frames, so we only need to
  // prefetch the next section's image once the gate completes.
  useEffect(() => {
    if (heroReady) {
      idlePrefetchImages([
        FRAMES[1],
        FRAMES[2],
        FRAMES[3],
        FRAMES[4],
        SCENE_01_IMG,
      ]);
    } else if (mobileGate) {
      idlePrefetchImages([SCENE_01_IMG]);
    }
  }, [heroReady, mobileGate]);

  // Desktop animation timeline — unchanged.
  useEffect(() => {
    if (skipIntro || isMobile) return;
    if (!heroReady) return;
    if (reducedMotion) {
      setFrame(5);
      setEntered(true);
      setDescriptorOut(true);
      setBrandTop(true);
      setNavVisible(true);
      setRevealed(true);
      setCtaVisible(true);
      setScrollCueVisible(true);
      return;
    }
    const H = DESKTOP_FIRST_FRAME_HOLD_MS;
    const timers = [
      // Brand intro
      setTimeout(() => setEntered(true), 300),
      // Image crossfade sequence (runs in background behind the title card)
      setTimeout(() => setFrame(2), 1200 + H),
      setTimeout(() => setFrame(3), 2800 + H),
      // Descriptor fades out before the brand moves
      setTimeout(() => setDescriptorOut(true), 2700 + H),
      // Brand travels into the nav
      setTimeout(() => setBrandTop(true), 3200 + H),
      setTimeout(() => setFrame(4), 4400 + H),
      setTimeout(() => setFrame(5), 6200 + H),
      // Final caption + CTA after the room has lit
      setTimeout(() => setRevealed(true), 8600 + H),
      setTimeout(() => setCtaVisible(true), 9000 + H),
      // Quiet scroll cue — appears ~900ms after the hero headline has
      // settled, so it never competes with the headline reveal.
      setTimeout(() => setScrollCueVisible(true), 9500 + H),
      // Nav items appear only AFTER the "Scroll to unfold" cue has
      // arrived — the brand center, then headline, then cue, then nav.
      setTimeout(() => setNavVisible(true), 9900 + H),
    ];
    return () => timers.forEach(clearTimeout);
  }, [reducedMotion, skipIntro, heroReady, isMobile]);

  // Mobile animation timeline — runs only AFTER the visual-readiness
  // gate completes. Sequence mirrors desktop but compressed to ≈3.8s.
  // The brand wordmark is already visible from first paint (locked
  // initial state), so we don't re-fire `entered` here.
  useEffect(() => {
    if (!isMobile || skipIntro || reducedMotion) return undefined;
    if (!mobileGate) return undefined;
    const timers = [
      setTimeout(() => setFrame(2), MOBILE_T.frame2),
      setTimeout(() => setFrame(3), MOBILE_T.frame3),
      setTimeout(() => setDescriptorOut(true), MOBILE_T.descriptorOut),
      setTimeout(() => setBrandTop(true), MOBILE_T.brandTop),
      setTimeout(() => setFrame(4), MOBILE_T.frame4),
      setTimeout(() => setFrame(5), MOBILE_T.frame5),
      setTimeout(() => setRevealed(true), MOBILE_T.revealed),
      setTimeout(() => setCtaVisible(true), MOBILE_T.revealed),
      setTimeout(() => setScrollCueVisible(true), MOBILE_T.scrollCue),
      setTimeout(() => setNavVisible(true), MOBILE_T.navVisible),
    ];
    return () => timers.forEach(clearTimeout);
  }, [isMobile, skipIntro, reducedMotion, mobileGate]);

  // SPA scroll-restoration catcher.
  //
  // The browser restores the saved scroll position at different moments
  // depending on the page's height at each layout pass. For short
  // sections (e.g. Scenes / Philosophy) restoration usually happens on
  // the first frame. For deep sections that follow tall pinned-scroll
  // blocks (e.g. WE SHAPE), the page only reaches its full scrollHeight
  // after several layout passes — restoration can land 500–1500ms after
  // mount. We therefore listen across that whole window: synchronous +
  // 2 rAF + a series of timed checks + a scroll listener that catches
  // restoration the instant it happens. Each catch performs a single
  // atomic snap (skipIntro + brand-final state + navHidden + onLight in
  // one batched render commit) and toggles a "snap-instant" guard for
  // one paint so any mid-animation opacity values jump silently to 0.
  useLayoutEffect(() => {
    if (skipIntro) return;

    const NAV_Y = 70;
    const isInViewport = (testid) => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.9 && r.bottom > 0;
    };

    const snap = () => {
      if (!scrollIsPastHero()) return false;

      // Compute nav-visibility zone synchronously so it lands in the
      // same render commit as skipIntro — no one-frame flash. We treat
      // WE SHAPE as middle-story end-to-end EXCEPT for its final
      // "Begin a Conversation" CTA state — when the CTA itself has
      // faded in, the section is an action zone and the nav appears.
      const hero = document.querySelector('[data-testid="hero-section"]');
      const onHero = hero
        ? hero.getBoundingClientRect().bottom > NAV_Y
        : window.scrollY < window.innerHeight * 0.85;
      let inFinal = isInViewport("footer-section");
      if (!inFinal) {
        const cta = document.querySelector('[data-testid="we-shape-cta"]');
        if (cta) {
          const ctaOpacity = parseFloat(getComputedStyle(cta).opacity || "0");
          if (ctaOpacity > 0.4 && isInViewport("conversation-section")) {
            inFinal = true;
          }
        }
      }

      // Guard transitions for one paint so mid-fade values snap silently.
      setSnapInstant(true);
      setSkipIntro(true);
      setFrame(5);
      setEntered(true);
      setDescriptorOut(true);
      setBrandTop(true);
      setNavVisible(true);
      setRevealed(true);
      setCtaVisible(true);
      if (!onHero && !inFinal) setNavHidden(true);

      const phil = document.getElementById("philosophy-stage");
      if (phil) {
        const r = phil.getBoundingClientRect();
        if (r.top <= NAV_Y && r.bottom > NAV_Y) setOnLight(true);
      }
      return true;
    };

    if (snap()) return;

    const rafs = [];
    rafs.push(
      requestAnimationFrame(() => {
        if (snap()) return;
        rafs.push(requestAnimationFrame(() => snap()));
      })
    );
    // Tail checks — covers late scroll restoration after deep sections
    // (e.g. WE SHAPE) finish their first layout pass.
    const timers = [50, 150, 300, 600, 1000, 1500, 2000].map((ms) =>
      setTimeout(snap, ms)
    );
    // Real-time catch: the moment the browser restores scroll, snap.
    // Auto-removes after 2.2s so it can never interfere with a user's
    // own scrolling later in the session.
    const onScroll = () => {
      if (snap()) window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const offTimer = setTimeout(() => {
      window.removeEventListener("scroll", onScroll);
    }, 2200);
    // Belt-and-braces: also retry once the document fully loads.
    const onLoad = () => snap();
    window.addEventListener("load", onLoad, { once: true });

    return () => {
      rafs.forEach((id) => cancelAnimationFrame(id));
      timers.forEach(clearTimeout);
      clearTimeout(offTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("load", onLoad);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Release the snap-instant guard on the next paint, so future genuine
  // transitions (e.g. nav fading back in at the final CTA) still animate.
  useEffect(() => {
    if (!snapInstant) return;
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setSnapInstant(false));
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [snapInstant]);

  // Graceful fallback — if the user scrolls before the hero's timed
  // reveal sequence has finished, snap the nav (and the cue's exit) to
  // their final visible state immediately so the page never looks
  // half-built.
  useEffect(() => {
    const onAnyScroll = () => {
      setNavVisible(true);
      window.removeEventListener("scroll", onAnyScroll);
      window.removeEventListener("wheel", onAnyScroll);
      window.removeEventListener("touchmove", onAnyScroll);
    };
    window.addEventListener("scroll", onAnyScroll, { passive: true, once: true });
    window.addEventListener("wheel", onAnyScroll, { passive: true, once: true });
    window.addEventListener("touchmove", onAnyScroll, { passive: true, once: true });
    return () => {
      window.removeEventListener("scroll", onAnyScroll);
      window.removeEventListener("wheel", onAnyScroll);
      window.removeEventListener("touchmove", onAnyScroll);
    };
  }, []);

  // Fade the scroll cue out the moment the user scrolls, and never show
  // it again for this session.
  useEffect(() => {
    if (!scrollCueVisible) return;
    const onFirstScroll = () => {
      setScrollCueVisible(false);
      window.removeEventListener("scroll", onFirstScroll);
      window.removeEventListener("wheel", onFirstScroll);
      window.removeEventListener("touchmove", onFirstScroll);
      window.removeEventListener("keydown", onKey);
    };
    const onKey = (e) => {
      if (
        ["PageDown", "ArrowDown", "Space", " ", "End", "Home"].includes(e.key)
      ) {
        onFirstScroll();
      }
    };
    window.addEventListener("scroll", onFirstScroll, { passive: true, once: true });
    window.addEventListener("wheel", onFirstScroll, { passive: true, once: true });
    window.addEventListener("touchmove", onFirstScroll, { passive: true, once: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onFirstScroll);
      window.removeEventListener("wheel", onFirstScroll);
      window.removeEventListener("touchmove", onFirstScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [scrollCueVisible]);

  // Navbar/wordmark behaviour — refined for the homepage story:
  //   • Visible only in the Hero zone and the final CTA / footer zone.
  //   • Hidden across the entire mid-story body (Philosophy → WE SHAPE),
  //     even on scroll-up, so it never competes with the Chapter Pill
  //     for top-of-page orientation.
  //   • Also drives the dark-on-light palette while the strip sits over
  //     the ivory Philosophy stage (still computed so the colour state is
  //     correct when the nav fades back in at the final CTA).
  useEffect(() => {
    const NAV_Y = 70;
    // Small vertical buffer around the Hero bottom and the final CTA top so
    // tiny scroll wobbles around section boundaries don't flicker the nav.
    const EDGE_BUFFER = 24;

    const isInViewport = (testid) => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.9 && r.bottom > 0;
    };

    const onScroll = () => {
      const hero = document.querySelector('[data-testid="hero-section"]');
      const phil = document.getElementById("philosophy-stage");

      if (phil) {
        const rect = phil.getBoundingClientRect();
        setOnLight(rect.top <= NAV_Y && rect.bottom > NAV_Y);
      }

      // On the hero — always show the nav (with a tiny buffer past its bottom
      // so the fade-out triggers cleanly once we're truly into the story).
      const onHero = hero
        ? hero.getBoundingClientRect().bottom > NAV_Y - EDGE_BUFFER
        : window.scrollY < window.innerHeight * 0.85;
      if (onHero) {
        setNavHidden(false);
        return;
      }

      // The footer (future P2 section) is the only zone after the
      // mid-story body where the top nav should re-appear. The "Begin a
      // Conversation" CTA lives INSIDE the WE SHAPE pinned journey, so
      // WE SHAPE is treated as middle-story end-to-end — the Chapter
      // Pill stays the only top-level orientation element here.
      if (isInViewport("footer-section")) {
        setNavHidden(false);
        return;
      }

      // …with one explicit exception: the WE SHAPE *final* CTA state.
      // While WE SHAPE is mid-reveal the nav stays hidden, but once the
      // "Begin a Conversation" CTA itself has visibly faded in (its CSS
      // opacity is driven by the section's `--cp` progress var, and
      // also forced to 1 in the static / reduced-motion fallback), the
      // section has become an action zone — so we surface the top nav
      // and let the Chapter Pill step aside (it already hides whenever
      // conversation-section is in the viewport).
      const cta = document.querySelector('[data-testid="we-shape-cta"]');
      if (cta) {
        const ctaOpacity = parseFloat(getComputedStyle(cta).opacity || "0");
        if (ctaOpacity > 0.4 && isInViewport("conversation-section")) {
          setNavHidden(false);
          return;
        }
      }

      // Mid-story (Philosophy → WE SHAPE inclusive) — keep the nav
      // hidden so the Chapter Pill remains the only top-level
      // orientation element. We intentionally do NOT reveal on
      // scroll-up here, and we do NOT treat WE SHAPE's internal
      // conversation block as a final CTA zone.
      setNavHidden(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const scrollToPhilosophy = () => {
    const el = document.getElementById("philosophy-stage");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Brand wordmark click — smooth-scroll back to the very top of the
  // page (the Hero). The Hero intro is not replayed because all of
  // its state flags (`entered`, `brandTop`, etc.) have already settled
  // by the time the brand has reached the navbar, and they are never
  // reset; on returning to scrollY=0 the regular scroll handler simply
  // re-surfaces the nav in its Hero state.
  const goHome = (e) => {
    e?.preventDefault?.();
    const behavior = reducedMotion ? "auto" : "smooth";
    window.scrollTo({ top: 0, left: 0, behavior });
  };

  const goToWork = (e) => {
    e.preventDefault();
    navigate("/work");
  };

  const goToEnquire = (e) => {
    e.preventDefault();
    navigate("/enquire");
  };

  const goToCareers = (e) => {
    e.preventDefault();
    navigate("/careers");
  };

  const goToPress = (e) => {
    e.preventDefault();
    navigate("/press");
  };

  return (
    <main className="hls" data-testid="hero-light-study-root">
      <section
        ref={heroRef}
        className="hls__hero"
        data-testid="hero-section"
        aria-label="Hero light study — the scene slowly receiving light"
      >
        {/* Stacked image layers */}
        {FRAMES.map((src, i) => {
          const idx = i + 1;
          const active = frame >= idx;
          const duration = DURATIONS[i] || 1600;
          return (
            <div
              key={src}
              className={["hls__layer", active ? "is-active" : ""].join(" ")}
              data-testid={`hls-frame-${idx}`}
              data-state={active ? "active" : "pending"}
              style={{
                backgroundImage: `url(${src})`,
                zIndex: idx,
                transitionDuration: idx === 1 ? "0ms" : `${duration}ms`,
              }}
              role={idx === 5 ? "img" : undefined}
              aria-label={
                idx === 5
                  ? "Warm wall with soft foliage-like daylight pattern and layered material detail"
                  : undefined
              }
            />
          );
        })}

        {/* Subtle warm overlay only for caption legibility */}
        <div className="hls__overlay" aria-hidden="true" />

        {/* Final caption — revealed only after the room has lit.
            The scroll cue lives inside this same wrapper so the gap
            between the headline and "Scroll to unfold" is a single
            controlled clamp() value (not a viewport-height
            percentage), keeping the relationship consistent across
            screen sizes. */}
        <div className="hls__copy" data-testid="hls-copy">
          <h2
            className={["hls__headline", revealed ? "is-visible" : ""].join(" ")}
            data-testid="hls-headline"
          >
            Homes composed in light, detail, and drama.
          </h2>

          {/* Quiet scroll cue — sits below the headline at a clamped
              distance. Non-interactive, no button styling. */}
          <div
            className={[
              "hls__scroll-cue",
              scrollCueVisible ? "is-visible" : "",
            ].join(" ")}
            aria-hidden="true"
            data-testid="hls-scroll-cue"
          >
            <span className="hls__scroll-cue-text">Scroll to unfold</span>
            <span className="hls__scroll-cue-line" />
            <span className="hls__scroll-cue-dot" />
          </div>
        </div>
      </section>

      {/* Brand mark + nav are hoisted OUT of the hero <section>. The hero
          uses `isolation: isolate` (a new stacking context) so any
          position:fixed children would be trapped inside it and covered
          by later sections. Lifting them up makes them stack at the
          document root. */}
      <div
        className={[
          "hls__brand-mark",
          entered ? "is-entered" : "",
          descriptorOut ? "descriptor-out" : "",
          brandTop ? "is-top" : "",
          onLight ? "is-on-light" : "",
          navHidden ? "is-hidden" : "",
          snapInstant ? "is-snap-instant" : "",
        ].join(" ")}
        data-testid="hls-brand-mark"
        data-state={brandTop ? "top" : "center"}
      >
        <div className="hls__brand-inner">
          <h1 className="hls__brand-name" data-testid="hls-brand-name">
            <button
              type="button"
              className="hls__brand-name-btn brand-wordmark"
              onClick={goHome}
              aria-label="Return to top"
              data-testid="hls-brand-home"
            >
              Ōraṇi
            </button>
          </h1>
        </div>
        <p className="hls__brand-sub" data-testid="hls-brand-sub">
          Interiors, Architecture &amp; Experiential Design
        </p>
      </div>

      <nav
        className={[
          "hls__nav",
          navVisible ? "is-visible" : "",
          onLight ? "is-on-light" : "",
          navHidden ? "is-hidden" : "",
          snapInstant ? "is-snap-instant" : "",
          mobileMenuOpen ? "has-menu-open" : "",
        ].join(" ")}
        aria-label="Primary"
        data-testid="hls-nav"
      >
        <ul className="hls__nav-list hls__nav-list--left">
          <li><a href="/work" onClick={goToWork} data-testid="hls-nav-work">Work</a></li>
          <li><a href="#process-section" onClick={(e) => { e.preventDefault(); scrollToId("process-section"); }} data-testid="hls-nav-process">Process</a></li>
          <li><a href="/press" onClick={goToPress} data-testid="hls-nav-press">Press</a></li>
        </ul>

        <div className="hls__nav-brand-slot" aria-hidden="true" />

        <ul className="hls__nav-list hls__nav-list--right">
          <li><a href="#studio" onClick={(e) => { e.preventDefault(); scrollToId("studio"); }} data-testid="hls-nav-studio">Studio</a></li>
          <li><a href="/enquire" onClick={goToEnquire} data-testid="hls-nav-enquire">Enquire</a></li>
          <li><a href="/careers" onClick={goToCareers} data-testid="hls-nav-careers">Careers</a></li>
        </ul>

        {/* Mobile/tablet-only trigger — hidden by CSS above 880px.
            Icon-only: two thin parchment bars that morph into a
            close X while the overlay is open. */}
        <button
          type="button"
          className="hls__nav-menu-btn"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="hls-mobile-menu"
          onClick={() => setMobileMenuOpen((s) => !s)}
          data-testid="hls-nav-menu"
        >
          <span className="hls__nav-menu-icon" aria-hidden="true">
            <span className="hls__nav-menu-icon-bar" />
            <span className="hls__nav-menu-icon-bar" />
          </span>
        </button>
      </nav>

      <HlsMobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onScrollTo={scrollToId}
        onNavigate={(to) => navigate(to)}
      />

      <PhilosophyStage />

      <ScenesOfHomes />

      <RecognitionPress />

      <ProcessTable />

      <Studio />

      <WeShape />

      <Footer />

      {/* Quiet editorial chapter marker — momentary orientation cue,
          driven by chapter-change + scroll-up intent. */}
      <ChapterPill />
    </main>
  );
}
